import { authError, dbError } from './logger';

interface SupabaseError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

/**
 * Custom Postgres SQLSTATE raised by the `enforce_check_in_cooldown` trigger when the
 * same member is checked in again within 60 seconds. Surfaced to the client as `error.code`.
 * Migration: 20260610000000_add_check_in_cooldown.sql
 */
export const CHECK_IN_COOLDOWN_ERRCODE = 'HK429';

/** User-facing message shown when a check-in is rejected by the 60-second cooldown. */
export const CHECK_IN_COOLDOWN_MESSAGE =
  'Checked in just now — please wait 60 seconds before checking in again.';

/**
 * True if the error is a check-in cooldown rejection. Recognizes both the raw PostgREST
 * error (by SQLSTATE `HK429`) and the already-wrapped Error (by its message), so it works
 * whether called on the original DB error or after a hook has re-thrown it.
 */
export function isCheckInCooldownError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  return e.code === CHECK_IN_COOLDOWN_ERRCODE || e.message === CHECK_IN_COOLDOWN_MESSAGE;
}

/**
 * True if the error is an authorization denial — Postgres `42501`
 * (insufficient_privilege). Used by hooks that read the super-admin-only
 * staff ledgers and treat a denial as "no rows for this viewer" rather than
 * a failure (the F&B report convention).
 *
 * Deliberately does NOT match `PGRST301`: that is a JWT validation failure
 * (expired/invalid session), not a permission answer — it must flow to
 * handleSupabaseError's session-expired path instead of being silently
 * converted to zero rows, which would undercount totals for a super_admin
 * whose session merely expired.
 */
export function isPermissionDenied(err: { code?: string } | null | undefined): boolean {
  return err?.code === '42501';
}

interface ErrorContext {
  operation: string;
  params?: Record<string, unknown>;
}

/**
 * Detects transient network errors (Safari "Load failed", Chrome "Failed to fetch")
 * and Safari-specific DOMException / StorageUnknownError from Supabase SDK.
 * These are non-actionable and should not be sent to Sentry.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    const s = String(error ?? '');
    return s === 'Load failed' || s === 'Failed to fetch' || s === 'NetworkError';
  }

  const errObj = error as Record<string, unknown>;
  const msg = String(errObj.message ?? '');

  // Safari / Chrome network errors
  if (
    msg === 'Load failed' ||
    msg === 'Failed to fetch' ||
    msg === 'NetworkError' ||
    msg.includes('TypeError: Load failed') ||
    msg.includes('TypeError: Failed to fetch') ||
    // Re-thrown form from handleSupabaseError itself — recognize the helper's
    // own translation so outer catches don't re-log the same network failure.
    msg.includes('A network error occurred. Please check your connection')
  ) {
    return true;
  }

  // Safari DOMException via Supabase Storage SDK (StorageUnknownError wrapping DOMException)
  if (
    errObj.name === 'StorageUnknownError' ||
    errObj.originalError instanceof DOMException ||
    msg.includes('did not match the expected pattern')
  ) {
    return true;
  }

  return false;
}

/**
 * Handles standard Supabase query errors with consistent JWT/generic error handling.
 *
 * Replaces the repeated if/else JWT check pattern across all hooks:
 * - JWT expired → logs authError + throws session expired message
 * - Other errors → logs dbError + throws user-friendly message
 *
 * @throws Always throws — either a session expired or a "Failed to {operation}" error.
 */
export function handleSupabaseError(
  error: SupabaseError,
  { operation, params }: ErrorContext,
): never {
  if (isTransientNetworkError(error)) {
    // Log to console only — not Sentry (these are non-actionable)
    console.warn(`[Network] Transient error during ${operation}`, params ?? '');
    throw new Error('A network error occurred. Please check your connection and try again.');
  }

  if (error.message === 'JWT expired') {
    authError(`JWT expired while ${operation}`, error, params);
    throw new Error('Your session has expired. Please sign in again.');
  } else {
    dbError(`Error ${operation}`, error, params);
    throw new Error(`Failed to ${operation.replace(/^(fetching|loading|checking|creating|updating|deleting)/, (m) => {
      const map: Record<string, string> = {
        fetching: 'load',
        loading: 'load',
        checking: 'check',
        creating: 'create',
        updating: 'update',
        deleting: 'delete',
      };
      return map[m] || m;
    })}`);
  }
}

/**
 * Handles unexpected errors in catch blocks with consistent logging and re-throwing.
 *
 * Replaces the repeated 5-line catch block pattern across all hooks:
 * - If the error is already an Error instance, re-throws its message
 * - Otherwise logs and throws a generic "unexpected error" message
 *
 * @throws Always throws — preserves original Error messages, wraps unknown errors.
 */
/**
 * A refusal the user is meant to READ, not an incident to investigate.
 *
 * The database distinguishes these by SQLSTATE 22023 — a rule the caller broke, with
 * a sentence written for a human. Without a way to say so, such a refusal reaches
 * `handleUnexpectedError` and files a Sentry issue, so an ordinary "that member
 * already has a booth then" at the front desk looks identical to a fault. Throw this
 * instead and it reaches the user's toast untouched and unreported.
 */
export class RuleRejectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleRejectionError';
  }
}

export function handleUnexpectedError(
  error: unknown,
  { hookName, operation, params }: { hookName: string; operation: string; params?: Record<string, unknown> },
): never {
  // An expected refusal, already phrased for the reader. Pass it through without
  // reporting: it is the system working.
  if (error instanceof RuleRejectionError) {
    throw error;
  }

  if (isTransientNetworkError(error)) {
    console.warn(`[Network] Transient error in ${hookName}`, params ?? '');
    throw new Error('A network error occurred. Please check your connection and try again.');
  }

  dbError(`Unexpected error in ${hookName}`, error, params);
  throw new Error(
    error instanceof Error
      ? error.message
      : `An unexpected error occurred while ${operation}`
  );
}

/**
 * Maps Supabase errors to user-friendly Error objects without throwing.
 *
 * Unlike `handleSupabaseError` (which always throws), this returns an Error
 * instance — useful when the caller needs to throw it themselves or pass it
 * along (e.g. in React Query error boundaries that expect Error objects).
 *
 * Handles:
 * - JWT expired → session expired message
 * - Known operation contexts → specific user-friendly messages
 * - Fallback messages based on operation keywords (fetch/create/update/delete)
 */
export function mapSupabaseError(
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>
): Error {
  if (!error) {
    return new Error('Unknown error occurred');
  }

  const errorObj = error as { message?: string };

  // Handle transient network errors (no Sentry)
  if (isTransientNetworkError(errorObj)) {
    console.warn(`[Network] Transient error in ${context}`, metadata ?? '');
    return new Error('A network error occurred. Please check your connection and try again.');
  }

  // Handle JWT expiration errors
  if (errorObj.message === 'JWT expired') {
    authError(`JWT expired in ${context}`, error, metadata);
    return new Error('Your session has expired. Please sign in again.');
  }

  // Handle generic database errors
  dbError(`Error in ${context}`, error, metadata);

  // Return user-friendly error message based on context
  const errorMessage = getContextualErrorMessage(context, errorObj);
  return new Error(errorMessage);
}

/**
 * Gets contextual error messages based on the operation context
 */
function getContextualErrorMessage(context: string, error: { message?: string }): string {
  const contextMessages: Record<string, string> = {
    'fetching webcam photos': 'Failed to fetch member photos',
    'fetching profile photos': 'Failed to fetch member profile photos',
    'fetching all webcam photos': 'Failed to fetch member photos',
    'fetching all profile photos': 'Failed to fetch member profile photos',
    'creating webcam photo': 'Failed to create photo record',
    'updating profile photo': 'Failed to update profile photo',
    'deleting photo': 'Failed to delete photo',
  };

  // Try to find a specific message for the context
  const specificMessage = contextMessages[context.toLowerCase()];
  if (specificMessage) {
    return specificMessage;
  }

  // Fallback to generic messages based on operation type
  if (context.includes('fetch') || context.includes('load')) {
    return 'Failed to load data. Please try again.';
  }

  if (context.includes('create') || context.includes('insert')) {
    return 'Failed to create record. Please try again.';
  }

  if (context.includes('update') || context.includes('edit')) {
    return 'Failed to update record. Please try again.';
  }

  if (context.includes('delete') || context.includes('remove')) {
    return 'Failed to delete record. Please try again.';
  }

  // Ultimate fallback
  return error.message || 'An unexpected error occurred. Please try again.';
}
